import type { MediaGroup } from "@/openapi/generated/models";
import { MediaGroup as MediaGroupEnum } from "@/openapi/generated/models";

export type ImageUploadPreset = "profile" | "cover" | "logo";

interface PresetConfig {
  maxWidth: number;
  maxHeight: number;
  quality: number;
  mimeType: "image/webp" | "image/jpeg";
}

export const IMAGE_UPLOAD_PRESETS: Record<ImageUploadPreset, PresetConfig> = {
  profile: {
    maxWidth: 600,
    maxHeight: 800,
    quality: 0.82,
    mimeType: "image/webp",
  },
  cover: {
    maxWidth: 1600,
    maxHeight: 900,
    quality: 0.82,
    mimeType: "image/webp",
  },
  logo: {
    maxWidth: 800,
    maxHeight: 800,
    quality: 0.85,
    mimeType: "image/webp",
  },
};

/** Skip re-encoding when the file is already small enough for web display. */
const OPTIMIZED_SIZE_BYTES = 200 * 1024;

export function scaleToFit(
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number,
): { width: number; height: number } {
  if (width <= maxWidth && height <= maxHeight) {
    return { width, height };
  }

  const ratio = Math.min(maxWidth / width, maxHeight / height);
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
}

export function replaceImageExtension(filename: string, extension: string): string {
  const base = filename.replace(/\.[^.]+$/, "") || filename;
  return `${base}.${extension}`;
}

export function getImageUploadPresetFromMediaGroup(
  mediaGroup: MediaGroup,
): ImageUploadPreset | null {
  if (mediaGroup === MediaGroupEnum.Profile) {
    return "profile";
  }
  if (mediaGroup === MediaGroupEnum.Cover) {
    return "cover";
  }
  if (mediaGroup === MediaGroupEnum.Logo) {
    return "logo";
  }
  return null;
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob(resolve, mimeType, quality);
  });
}

/**
 * Resize and compress raster images before upload. SVG and already-optimized
 * files are returned unchanged. Falls back to the original file on failure.
 */
export async function compressImageForUpload(
  file: File,
  preset: ImageUploadPreset,
): Promise<File> {
  if (file.type === "image/svg+xml") {
    return file;
  }

  const config = IMAGE_UPLOAD_PRESETS[preset];

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, {
      imageOrientation: "from-image",
    });
  } catch {
    return file;
  }

  const needsResize =
    bitmap.width > config.maxWidth || bitmap.height > config.maxHeight;
  const isAlreadyOptimized =
    preset !== "logo" &&
    !needsResize &&
    file.size <= OPTIMIZED_SIZE_BYTES &&
    (file.type === "image/webp" || file.type === "image/jpeg");

  if (isAlreadyOptimized) {
    bitmap.close();
    return file;
  }

  const { width: targetWidth, height: targetHeight } = scaleToFit(
    bitmap.width,
    bitmap.height,
    config.maxWidth,
    config.maxHeight,
  );

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const context = canvas.getContext("2d");
  if (!context) {
    bitmap.close();
    return file;
  }

  context.drawImage(bitmap, 0, 0, targetWidth, targetHeight);
  bitmap.close();

  const blob = await canvasToBlob(canvas, config.mimeType, config.quality);
  if (!blob || blob.size >= file.size) {
    return file;
  }

  const extension = config.mimeType === "image/webp" ? "webp" : "jpg";
  return new File([blob], replaceImageExtension(file.name, extension), {
    type: config.mimeType,
    lastModified: Date.now(),
  });
}
